// ===== GPS (geolocalización) =====
// Única puerta de entrada a navigator.geolocation. Con la preferencia apagada
// no se llama a la API: no se pide permiso ni se consulta la ubicación.
//
// Preferencia: localStorage `sp_gps` = '1' (activo) | '0' (desactivado).
// Sin valor = activo, para no cambiar nada a quien ya usaba la app.
// La app nativa (Tauri) lee el mismo contrato: docs/mobile/08-preferencia-gps.md

export const GPS_KEY = 'sp_gps'

export function gpsEnabled(): boolean {
  try { return localStorage.getItem(GPS_KEY) !== '0' } catch { return true }
}

export function setGpsEnabled(on: boolean): void {
  try { localStorage.setItem(GPS_KEY, on ? '1' : '0') } catch { /* modo privado / storage bloqueado */ }
}

// ----- Sincronización con la cuenta (users.gps_enabled) -----
// Cada cambio del interruptor se escribe en local y en el servidor. Si el PUT
// falla (sin red), `sp_gps_pending` marca que el valor local es más nuevo.
// Regla: pendiente o servidor sin valor → se sube el local; si no, manda el
// servidor (es la última elección explícita, hecha en cualquier dispositivo).
export const GPS_PENDING_KEY = 'sp_gps_pending'

function localGpsValue(): boolean | null {
  try { const v = localStorage.getItem(GPS_KEY); return v === null ? null : v !== '0' } catch { return null }
}

export function setGpsSyncPending(pending: boolean): void {
  try { pending ? localStorage.setItem(GPS_PENDING_KEY, '1') : localStorage.removeItem(GPS_PENDING_KEY) } catch {}
}

function gpsSyncPending(): boolean {
  try { return localStorage.getItem(GPS_PENDING_KEY) === '1' } catch { return false }
}

/**
 * Concilia el valor de la cuenta con el local.
 * `server` undefined = API sin el campo (no se hace nada).
 * Devuelve si cambió el valor local y, si hay que subir el local, cuál.
 */
export function reconcileGpsPref(server: boolean | null | undefined): { changed: boolean; push: boolean | null } {
  if (server === undefined) return { changed: false, push: null }
  const local = localGpsValue()
  if (gpsSyncPending() || server === null) {
    if (local === null || local === server) { setGpsSyncPending(false); return { changed: false, push: null } }
    return { changed: false, push: local }
  }
  if (local === server) return { changed: false, push: null }
  setGpsEnabled(server)
  return { changed: (local ?? true) !== server, push: null }
}

export function geoSupported(): boolean {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator
}

export type GeoPermission = 'granted' | 'denied' | 'prompt' | 'unsupported' | 'unknown'
export type GeoFix = { lat: number; lng: number; accuracy: number }
export type GeoResult = { fix: GeoFix | null; error?: 'disabled' | 'unsupported' | 'denied' | 'unavailable' | 'timeout' }

// iOS < 16 no tiene Permissions API y en la PWA de iOS `query()` puede seguir
// diciendo 'prompt' tras un rechazo: el error PERMISSION_DENIED es la señal fiable.
let _deniedSeen = false

export async function geoPermission(): Promise<GeoPermission> {
  if (!geoSupported()) return 'unsupported'
  let state: GeoPermission = 'unknown'
  try {
    const st = await navigator.permissions?.query({ name: 'geolocation' as PermissionName })
    if (st) state = st.state as GeoPermission
  } catch { /* Safari antiguo: query() lanza con 'geolocation' */ }
  if (state !== 'granted' && _deniedSeen) return 'denied'
  return state
}

/** Avisa cuando el usuario cambia el permiso del sistema (si el navegador lo soporta). */
export function onGeoPermissionChange(cb: () => void): void {
  try {
    navigator.permissions?.query({ name: 'geolocation' as PermissionName })
      .then(st => st.addEventListener('change', () => { if (st.state === 'granted') _deniedSeen = false; cb() }))
      .catch(() => {})
  } catch { /* sin Permissions API */ }
}

/**
 * Pide una posición puntual. No rastrea (sin watchPosition).
 * getCurrentPosition se invoca de forma síncrona: si se llama desde un gesto
 * del usuario, iOS muestra el diálogo de permiso ligado a ese gesto.
 */
export function requestFix(timeout = 10000): Promise<GeoResult> {
  if (!gpsEnabled()) return Promise.resolve({ fix: null, error: 'disabled' })
  if (!geoSupported()) return Promise.resolve({ fix: null, error: 'unsupported' })
  return new Promise(resolve => {
    try {
      navigator.geolocation.getCurrentPosition(pos => {
        _deniedSeen = false
        // Si el usuario lo apagó mientras esperábamos, se descarta la posición.
        if (!gpsEnabled()) return resolve({ fix: null, error: 'disabled' })
        resolve({ fix: { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy } })
      }, err => {
        if (err.code === err.PERMISSION_DENIED) _deniedSeen = true
        resolve({ fix: null, error: err.code === err.PERMISSION_DENIED ? 'denied' : err.code === err.TIMEOUT ? 'timeout' : 'unavailable' })
      }, { enableHighAccuracy: true, timeout, maximumAge: 60000 })
    } catch { resolve({ fix: null, error: 'unavailable' }) }
  })
}

/** Dónde cambiar el permiso del sistema, según plataforma. */
export function geoPermissionHint(): string {
  const ua = navigator.userAgent
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  if (ios) return 'En iPhone: Ajustes › Privacidad y seguridad › Localización › Safari (sitios web) › "Al usar la app".'
  if (/Android/.test(ua)) return 'En Android: mantén pulsado el icono de la app › Información › Permisos › Ubicación, o el candado de la barra de direcciones.'
  return 'Permítelo en la configuración del sitio del navegador (icono junto a la dirección).'
}
